import PropTypes from "prop-types";
import { Fragment, memo, useCallback, useEffect, useRef, useState } from "react";
import isEmpty from "lodash/isEmpty";
import { FormattedMessage } from "react-intl";
import {
	ALERT_TYPE,
	BOOK_STATUS,
	OFFER_CONTRACT_TYPES,
	PAYMENT_ERROR_STATUS,
	PAYMENT_METHODS,
	RESOLUTION,
	TRANSPORTATION_TYPES,
	USABLE_CREDITS_STATUS,
} from "app/constants";
import { Form, useFormikContext } from "formik";
import { scrollToElement } from "app/utils/scroller";
import { sendTagOnInsuranceSelect } from "app/utils/analytics";
import ContactOurExperts from "app/pages/.shared/ContactOurExperts";
import PriceChangedWarningMessage from "app/pages/Booking/Payment/PriceChangedWarningMessage";
import "./payment.scss";
import { paymentTypesValues } from "app/utils/propTypes";
import { PushNotificationContainer } from "app/pages/.shared/PushNotification/PushNotificationContainer";
import LoadingPlaceholder from "app/pages/.shared/LoadingPlaceholder";
import Modal from "react-modal";
import PaymentContactContainer from "app/pages/Booking/Payment/PaymentContactContainer";
import PaymentCouponsContainer from "app/pages/Booking/Payment/PaymentCoupons/PaymentCouponsContainer";
import HolidaySummaryContainer from "app/pages/Booking/HolidaySummary/HolidaySummary/HolidaySummaryContainer";
import {
	isExternalPaymentError,
	isPaymentError,
	transformBookingContact,
} from "app/utils/paymentUtils";
import PaymentInsurancesSection from "app/pages/Booking/Payment/PaymentInsurancesSection";
import PaymentInfoSection from "app/pages/Booking/Payment/PaymentInfoSection";
import PaymentPassengersSection from "app/pages/Booking/Payment/PaymentPassengersSection/PaymentPassengersSection";
import PaymentCTASection from "app/pages/Booking/Payment/PaymentCTASection/PaymentCTASection";
import AlertMessage from "app/pages/.shared/AlertMessage";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import { clean } from "fast-clean";
import Button, { BUTTON_SIZES, BUTTON_VARIANTS } from "app/pages/.shared/form/Button";

const MODAL_STYLE = {
	overlay: {
		backgroundColor: "rgba(43, 56, 63, .65)",
		zIndex: 6,
	},
	content: {
		top: 0,
		right: 0,
		left: 0,
		bottom: 0,
		border: "none",
		background: "none",
		overflow: "hidden",
		padding: "50px",
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
	},
};
const Payment = ({
	booking = {},
	payment = {},
	resolution,
	productUri,
	dateOfBirthRequired,
	isChildrenAgeRequired,
	showFrequentTravellerCardInput,
	selectedPaymentType,
	shop = "",
	showInsurancesSection,
	assuranceMultirisquesSansMotifDocumentName,
	assuranceAnnulationSansMotifDocumentName,
	cartAmount,
	usableCouponsStatus = "",
	isOperatorMode,
	isStopover,
	brandDisplayName,
	rgpdMessagePayment = [],
	shouldGetProfileAtPayment,
	onAdyenComponentMounted = () => {},
	onCreditCardSubmit = () => {},
	onAdyenAdditionalDetails = () => {},
	updateBooking = () => {},
	total,
	priceChangeData = {},
	isUsaDestination = false,
	persistPaymentData = () => {},
	error = {},
	handleChangeValues = () => {},
	disablePassengers,
	setPaymentError = () => {},
}) => {
	const contactRef = useRef();
	const paymentErrorMessageRef = useRef();
	const paymentMethodsErrorMessageRef = useRef();
	const insuranceErrorMessageRef = useRef();
	const passengersErrorMessageRef = useRef();
	const termsErrorMessageRef = useRef();
	const errorMessageRef = useRef();
	const bookStatus = payment?.bookStatus;

	const { values, setStatus, setSubmitting, errors, isSubmitting } = useFormikContext();
	const [authoriseUpdateBooking, setAuthoriseUpdateBooking] = useState(false);

	// Pour résoudre le problème de synchronisation entre les valeurs actuelles et l'exécution de 'useEffect',
	// on utilise une référence  pour stocker les valeurs et garantir leur disponibilité constante.
	const valuesRef = useRef(values);

	useEffect(() => {
		return () => {
			// on souhaite persister les données saisies si on fait un back vers la page quote
			persistPaymentData(valuesRef.current);
		};
	}, []);

	const beforeValidateForm = () => {
		// on souhaite persister les données saisies si on revient sur la page paiement avant d'avoir la confirmation de réservation
		// exemple : reload, annulation depuis un partenaire externe et redirection sur la page de paiement, erreur de book
		updateBooking({
			paymentType: values?.payment?.type,
			contact: transformBookingContact(values?.contact),
			passengers: values?.passengers,
		});
	};

	useEffect(() => {
		valuesRef.current = values;
		if (authoriseUpdateBooking && !isEmpty(values)) {
			beforeValidateForm();
			setAuthoriseUpdateBooking(false);
		}
	}, [authoriseUpdateBooking, values]);

	useEffect(() => {
		if (Object.values(PAYMENT_ERROR_STATUS).includes(bookStatus)) {
			if (paymentErrorMessageRef?.current) {
				scrollToElement({ element: paymentErrorMessageRef.current, offset: -100 });
			}
		} else if (!isEmpty(clean(errors)) && isSubmitting) {
			scrollToErrorMessage();
		} else if (!isEmpty(error)) {
			scrollToElement({ element: errorMessageRef.current, offset: -100 });
		}
	}, [bookStatus, paymentErrorMessageRef?.current, error, errors, isSubmitting]);

	const handlePaymentCTASectionClick = () => {
		setAuthoriseUpdateBooking(true);
	};
	const scrollToErrorMessage = () => {
		let errorMessageRef;
		const errorsList = clean(errors);
		if (!isEmpty(errorsList?.contact)) {
			errorMessageRef = contactRef?.current;
		} else if (!isEmpty(errorsList?.passengers)) {
			errorMessageRef = passengersErrorMessageRef?.current;
		} else if (!isEmpty(errorsList?.insurance)) {
			errorMessageRef = insuranceErrorMessageRef?.current;
		} else if (!isEmpty(errorsList?.payment)) {
			errorMessageRef = paymentMethodsErrorMessageRef?.current;
		} else if (!isEmpty(errorsList?.terms)) {
			errorMessageRef = termsErrorMessageRef?.current;
		}
		if (errorMessageRef) {
			scrollToElement({ element: errorMessageRef, offset: -100 });
		}
	};

	const onInsuranceSelect = useCallback((data, insurance) => {
		// permet entre autre de mettre à jour le montant total de la réservation à la sélection de l'assurance
		updateBooking(data);

		sendTagOnInsuranceSelect(insurance);
	}, []);

	const isPassengersError =
		bookStatus === BOOK_STATUS.CHILDREN_AGES_DISCREPANCY ||
		bookStatus === BOOK_STATUS.INFANTS_AGES_DISCREPANCY;

	const isCouponsLoading =
		usableCouponsStatus === USABLE_CREDITS_STATUS.LOADING_CREDIT_NOTES ||
		usableCouponsStatus === USABLE_CREDITS_STATUS.LOADING_COUPONS;

	const isMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;

	const paymentTypes = payment?.paymentTypes;
	const isCheckoutPaymentInOneTermOnly =
		paymentTypes.length === 1 &&
		paymentTypes?.[0]?.paymentType === PAYMENT_METHODS.CHECKOUT &&
		paymentTypes?.[0]?.terms === 1;

	const isOperatorModeInOneTermOnly =
		isOperatorMode && paymentTypes?.[0]?.terms === 1 && paymentTypes?.length === 1;

	const displayPaymentSection =
		(cartAmount > 0 && !isCheckoutPaymentInOneTermOnly && !isOperatorModeInOneTermOnly) ||
		isCouponsLoading;

	return (
		<Fragment>
			<Form
				className="payment"
				ref={errorMessageRef}
				onChange={() => {
					handleChangeValues();
				}}
				noValidate
			>
				{isMobile && (
					<div className="payment__my-booking">
						<HolidaySummaryContainer showProductPicture />
					</div>
				)}

				{!isEmpty(error) &&
					isEmpty(clean(errors)) &&
					!isPaymentError(bookStatus) &&
					!isExternalPaymentError(bookStatus) &&
					!isPassengersError && (
						<div className="payment__error">
							<AlertMessage
								message={
									<FormattedMessage
										values={{
											link: (
												<Button
													variant={BUTTON_VARIANTS.LINK}
													size={BUTTON_SIZES.SMALL}
													onClick={() => window.history.back()}
													label={
														<FormattedMessage id="error.no.avaibilities.link.label" />
													}
												/>
											),
										}}
										{...error}
									/>
								}
							/>
						</div>
					)}

				{!isMobile && priceChangeData.isPriceChange && (
					<PriceChangedWarningMessage
						isTravelingByTrain={booking?.flight?.type === TRANSPORTATION_TYPES.TRAIN}
						offerType={booking?.offer?.type}
						newPrice={priceChangeData?.newPrice}
						oldPrice={priceChangeData?.oldPrice}
					/>
				)}

				{bookStatus === BOOK_STATUS.WARNING_FULL_PROPERTY ? (
					<ContactOurExperts
						message={<FormattedMessage id="confirmation.contact.headline.short" />}
					/>
				) : (
					<Fragment>
						<Fragment>
							<div
								className="payment__item"
								style={{ order: isOperatorMode ? "1" : "0" }}
							>
								<Typography
									variant={TYPOGRAPHY_VARIANTS.XL3}
									variantMD={TYPOGRAPHY_VARIANTS.XL4}
									isBold
									className="payment__title"
									component="h2"
								>
									<FormattedMessage id="payment.contact.title" />
								</Typography>

								<div className="payment__content" ref={contactRef}>
									<PaymentContactContainer
										shouldGetProfileAtPayment={shouldGetProfileAtPayment}
									/>
								</div>
							</div>

							{booking?.offer?.contractType !== OFFER_CONTRACT_TYPES.RENTAL &&
								!disablePassengers && (
									<div
										className="payment__item"
										ref={passengersErrorMessageRef}
										style={{ order: "2" }}
									>
										<PaymentPassengersSection
											bookStatus={bookStatus}
											resolution={resolution}
											productUri={productUri}
											dateOfBirthRequired={dateOfBirthRequired}
											showFrequentTravellerCardInput={
												showFrequentTravellerCardInput
											}
											isChildrenAgeRequired={isChildrenAgeRequired}
											showInsurancesSection={showInsurancesSection}
											warningMessage={isUsaDestination}
										/>
									</div>
								)}

							{showInsurancesSection && (
								<div
									className="payment__item"
									style={{ order: isOperatorMode ? "0" : "3" }}
								>
									<PaymentInsurancesSection
										payment={payment}
										resolution={resolution}
										shop={shop}
										assuranceMultirisquesSansMotifDocumentName={
											assuranceMultirisquesSansMotifDocumentName
										}
										assuranceAnnulationSansMotifDocumentName={
											assuranceAnnulationSansMotifDocumentName
										}
										total={total}
										refInsuranceErrorMessage={insuranceErrorMessageRef}
										onInsuranceSelect={onInsuranceSelect}
									/>
								</div>
							)}

							{
								<div className="payment__item" style={{ order: "4" }}>
									<PaymentCouponsContainer isStopover={isStopover} />
								</div>
							}

							{/* this error message only appears when checkout is the only payment type because normally errors ar handled in the PaymentInfoSection component */}
							{!isEmpty(clean(error)) &&
								!displayPaymentSection &&
								(isPaymentError || isExternalPaymentError) && (
									<div ref={paymentErrorMessageRef} style={{ order: "5" }}>
										<AlertMessage
											message={<FormattedMessage {...error} />}
											alertType={ALERT_TYPE.ERROR}
										/>
									</div>
								)}

							{displayPaymentSection && (
								<div className="payment__item" style={{ order: "5" }}>
									<PaymentInfoSection
										brandDisplayName={brandDisplayName}
										usableCouponsStatus={usableCouponsStatus}
										payment={payment}
										shop={shop}
										onAdyenComponentMounted={onAdyenComponentMounted}
										onCreditCardSubmit={(state, dropin, actions) => {
											onCreditCardSubmit(
												valuesRef.current,
												{
													setStatus,
													setSubmitting,
												},
												{
													state,
													dropin,
													actions,
												}
											);
										}}
										onAdyenAdditionalDetails={onAdyenAdditionalDetails}
										selectedPaymentType={selectedPaymentType}
										error={error}
										paymentErrorMessageRef={paymentErrorMessageRef}
										paymentMethodsErrorMessageRef={
											paymentMethodsErrorMessageRef
										}
										setPaymentError={setPaymentError}
										booking={booking}
									/>
								</div>
							)}
						</Fragment>

						<div
							className="payment__pay-button"
							data-testid="payment-pay-footer"
							style={{ order: "6" }}
							ref={termsErrorMessageRef}
						>
							<PaymentCTASection
								onClick={handlePaymentCTASectionClick}
								cartAmount={cartAmount}
								paymentType={selectedPaymentType}
								brandDisplayName={brandDisplayName}
								shouldDisableButton={isSubmitting}
								isOperatorMode={isOperatorMode}
								rgpdMessagePayment={rgpdMessagePayment}
							/>
						</div>
					</Fragment>
				)}
			</Form>
			<div className="payment__mandatory-label">
				<FormattedMessage id="payment.mandatory.fields.label" />
			</div>
			<PushNotificationContainer />
			<Modal isOpen={bookStatus === BOOK_STATUS.LOADING} style={MODAL_STYLE}>
				<div className="payment-modal__loading-placeholder">
					<LoadingPlaceholder
						message={<FormattedMessage id="payment.external.wait.message" />}
					/>
				</div>
			</Modal>
		</Fragment>
	);
};

Payment.propTypes = {
	booking: PropTypes.object,
	payment: PropTypes.object,
	resolution: PropTypes.string,
	productUri: PropTypes.string,
	dateOfBirthRequired: PropTypes.bool,
	isChildrenAgeRequired: PropTypes.bool,
	cartAmount: PropTypes.number,
	usableCouponsStatus: PropTypes.string,
	shop: PropTypes.string,
	total: PropTypes.number,
	boardDiscount: PropTypes.number,
	updateBooking: PropTypes.func,
	onAdyenComponentMounted: PropTypes.func,
	onCreditCardSubmit: PropTypes.func,
	onAdyenAdditionalDetails: PropTypes.func,
	showFrequentTravellerCardInput: PropTypes.bool,
	selectedPaymentType: paymentTypesValues,
	showInsurancesSection: PropTypes.bool,
	assuranceMultirisquesSansMotifDocumentName: PropTypes.string,
	assuranceAnnulationSansMotifDocumentName: PropTypes.string,
	isOperatorMode: PropTypes.bool,
	brandDisplayName: PropTypes.string,
	rgpdMessagePayment: PropTypes.array,
	shouldGetProfileAtPayment: PropTypes.bool,
	priceChangeData: PropTypes.object,
	persistPaymentData: PropTypes.func,
	isUsaDestination: PropTypes.bool,
	isStopover: PropTypes.bool,
	error: PropTypes.object,
	handleChangeValues: PropTypes.func,
	setPaymentError: PropTypes.func,
	disablePassengers: PropTypes.bool,
};

export default memo(Payment);
